import React from 'react';
import { styled, useTheme } from '@vw-marketing/us-components';
import { useDisclaimerContext } from '../../context/DisclaimerContext';
import Close from '../icons/Close';

interface Disclaimer {
  number: string;
  description: string | React.JSX.Element[];
  token: string;
}

interface DisclaimerModalProps {
  disclaimers: Disclaimer[];
}

const DisclaimerContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #000000bf;
  border-top: 1px solid #333333;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3); 
  z-index: 30;
  max-height: 200px;
  overflow-y: auto;
  transform: translateY(100%);
  transition: transform 0.3s ease-in-out;

  &.visible {
    transform: translateY(0);
  }
`;

const DisclaimerWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 15px 20px;
  gap: 15px;
`;

const DisclaimerContent = styled.div`
  flex: 1;
  font-size: 16px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.content.inverted};
`;

const DisclaimerItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 10px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const DisclaimerNumberContainer = styled.div`
  flex: 0 0 28px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.content.inverted};
`;

const DisclaimerText = styled.div`
  flex: 1;
  color: ${({ theme }) => theme.colors.content.inverted};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: transparent;
  z-index: 20;
  opacity: 1;
`;

export const DisclaimerModal: React.FC<DisclaimerModalProps> = ({
  disclaimers,
}) => {
  const { isVisible, activeDisclaimerNumber, hideDisclaimer } =
    useDisclaimerContext();
  const theme = useTheme();

  if (!disclaimers || disclaimers.length === 0) {
    return null;
  }

  const disclaimersToShow = activeDisclaimerNumber
    ? disclaimers.filter((d) => d.number === activeDisclaimerNumber)
    : disclaimers;

  const renderDisclaimerDescription = (
    description: string | React.JSX.Element[],
  ) => {
    if (typeof description === 'string') {
      return description;
    }
    return description;
  };

  return (
    <>
      {isVisible && <Overlay onClick={hideDisclaimer} />}

      <DisclaimerContainer className={isVisible ? 'visible' : ''}>
        <DisclaimerWrapper>
          <DisclaimerContent>
            {disclaimersToShow.map((disclaimer, index) => (
              <DisclaimerItem key={disclaimer.token || index}>
                <DisclaimerNumberContainer>
                  {disclaimer.number}.
                </DisclaimerNumberContainer>
                <DisclaimerText>
                  {renderDisclaimerDescription(disclaimer.description)}
                </DisclaimerText>
              </DisclaimerItem>
            ))}
          </DisclaimerContent>

          <CloseButton onClick={hideDisclaimer}>
            <Close iconSize={20} color={theme.colors.content.inverted} /> 
          </CloseButton>
        </DisclaimerWrapper>
      </DisclaimerContainer>
    </>
  );
};
